import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {
  type QueryKey,
  useSuspenseInfiniteQuery,
  type InfiniteData,
  type UseSuspenseInfiniteQueryResult,
} from '@github-ui/react-query'
import {reactFetch} from '@github-ui/verified-fetch'
import {parseLinkHeader} from '../../utils/parse-link-header'
import {
  type ThreadPreviewsCompPayload,
  isThreadPreviewComp,
  type ThreadPreviewsPayload,
  transformThreadPreviewToComp,
} from '../payloads/thread-previews'

export function threadPreviewsQueryKey(pathName: string): QueryKey {
  return [PageData.threadPreviews, pathName]
}

export type ThreadPreviewsPage = {
  data: ThreadPreviewsCompPayload
  nextUrl: string | null
}

export function threadPreviewsApiUrl(pathName: string): string {
  return `${pathName}/page_data/${PageData.threadPreviews}`
}

/**
 * Hook for fetching thread previews with pagination support.
 * Returns the infinite query result directly, allowing consumers to manage pagination.
 *
 * The API will only return a link to the next page if there are more pages to fetch and the feature flag is enabled.
 *
 * @param pathName - The base path for the API request
 * @returns The infinite query result with pagination controls
 */
export function useInfiniteThreadPreviewsPageData({
  pathName,
}: {
  pathName: string
}): UseSuspenseInfiniteQueryResult<InfiniteData<ThreadPreviewsPage>> {
  return useSuspenseInfiniteQuery<ThreadPreviewsPage, Error, InfiniteData<ThreadPreviewsPage>, QueryKey, string>({
    queryKey: threadPreviewsQueryKey(pathName),
    initialPageParam: threadPreviewsApiUrl(pathName),
    queryFn: async ({pageParam}: {pageParam: string}): Promise<ThreadPreviewsPage> => {
      const result = await reactFetch(pageParam)
      if (!result.ok) throw new Error(`Failed to fetch thread previews from ${pageParam}: HTTP ${result.status}`)

      const json = await result.json()
      const linkHeader = result.headers.get('Link')

      const nextUrl = parseLinkHeader(linkHeader, 'next')

      // Transform to ThreadPreviewsCompPayload if needed
      // Empty arrays are valid and don't need transformation - they cast safely to ThreadPreviewsCompPayload
      let data: ThreadPreviewsCompPayload
      if (json.length > 0 && !isThreadPreviewComp(json[0])) {
        data = (json as ThreadPreviewsPayload).map(transformThreadPreviewToComp)
      } else {
        data = json as ThreadPreviewsCompPayload
      }

      return {data, nextUrl}
    },
    getNextPageParam: (lastPage: ThreadPreviewsPage) => {
      return lastPage.nextUrl ?? undefined
    },
    staleTime: Infinity,
  })
}
